/**
 * Watermark động trên học liệu (plan 2026-10-03 — Sếp chốt dùng thay DRM).
 *
 * Trình duyệt KHÔNG phát hiện được phần mềm quay màn hình (OBS, Game Bar…), nên thay vì "đen
 * màn hình" kiểu DRM, mỗi khung hình đều mang danh tính người xem → bản quay bị phát tán truy
 * ra được người làm lộ (răn đe + truy vết):
 * - Lớp lát đều chữ mờ (antd `Watermark` — tự dựng lại nếu bị xoá/sửa qua DevTools).
 * - 1 dòng chữ rõ hơn nhảy vị trí ngẫu nhiên mỗi vài giây → khó cắt/che khi biên tập lại video.
 * Nội dung: họ tên · MSSV (không có MSSV → email) · ngày giờ hiện tại (cập nhật mỗi phút).
 *
 * Bật bằng `CONTENT_WATERMARK_ENABLED` (backend) → `user.feature_flags.content_watermark`.
 * PII hiển thị chỉ là của CHÍNH người đang xem, trên màn hình của họ.
 * Kèm lớp phủ đen `BlackoutOverlay` khi bật `CONTENT_BLACKOUT_ENABLED` (xem `ContentShield.tsx`).
 */
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { Watermark } from "antd";
import { BlackoutOverlay } from "./ContentShield";
import { useContentShield } from "./contentShieldContext";
import { useWatermarkLines } from "./watermarkLines";

const MOVE_INTERVAL_MS = 7000;

function randomPosition() {
  return { top: 5 + Math.random() * 78, left: 3 + Math.random() * 62 };
}

/** Dòng watermark nhảy vị trí ngẫu nhiên — đặt trong 1 khối `position: relative`. */
function MovingMark({ lines, dark }: { lines: string[]; dark: boolean }) {
  const [pos, setPos] = useState(randomPosition);
  useEffect(() => {
    const timer = window.setInterval(() => setPos(randomPosition()), MOVE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        top: `${pos.top}%`,
        left: `${pos.left}%`,
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 20,
        fontSize: 13,
        lineHeight: 1.35,
        whiteSpace: "nowrap",
        color: dark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.28)",
        textShadow: dark ? "0 0 2px rgba(0,0,0,0.6)" : "none",
      }}
    >
      {lines.map((l) => (
        <div key={l}>{l}</div>
      ))}
    </div>
  );
}

const TILE_PROPS = { gap: [140, 120] as [number, number], rotate: -22 };

/**
 * Bọc học liệu (bài học markdown, SCORM, tài liệu PDF…) bằng watermark + lớp che màn hình.
 * Cả 2 tính năng tắt → trả nguyên children.
 */
export function ContentWatermark({ children }: { children: ReactNode }) {
  const lines = useWatermarkLines();
  const shield = useContentShield();
  if (!lines && !shield) return <>{children}</>;
  const inner = (
    <div data-content-shield style={{ position: "relative" }}>
      {children}
      {lines && <MovingMark lines={lines} dark={false} />}
      <BlackoutOverlay />
    </div>
  );
  if (!lines) return inner;
  return (
    <Watermark content={lines} font={{ color: "rgba(0,0,0,0.07)", fontSize: 14 }} {...TILE_PROPS}>
      {inner}
    </Watermark>
  );
}

/**
 * Lớp watermark + che màn hình phủ lên video — đặt BÊN TRONG `<MediaPlayer>` để vẫn hiện khi
 * toàn màn hình. Watermark `pointer-events: none` → không chặn thanh điều khiển.
 */
export function VideoWatermark() {
  const lines = useWatermarkLines();
  const shield = useContentShield();
  if (!lines && !shield) return null;
  const layer: CSSProperties = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 50,
  };
  return (
    <div style={layer}>
      {lines && (
        <>
          <Watermark
            content={lines}
            font={{ color: "rgba(255,255,255,0.13)", fontSize: 14 }}
            {...TILE_PROPS}
            style={{ position: "absolute", inset: 0 }}
          >
            <div style={{ width: "100%", height: "100%" }} />
          </Watermark>
          <MovingMark lines={lines} dark />
        </>
      )}
      <BlackoutOverlay />
    </div>
  );
}
